/**
 * Relation rail layout (spec §8.15): a pure function of row geometry and edges — lane packing per
 * gutter, gutter widths, orthogonal paths with rounded corners, label and option-group positions, and
 * bundles: connectors of one track that share their origin or their target run along one trunk.
 * No DOM: the overlay only renders what this returns.
 *
 * Coordinates: y is the offset inside the virtualiser's spacer (row `start`); each gutter has its own
 * x axis. Left gutter: x = 0 at its outer edge, x = `leftGutter` where the content begins. Right
 * gutter: x = 0 where the content ends, x = `rightGutter` at its outer edge.
 */

export type Side = "left" | "right";
export type Dash = "solid" | "dashed" | "dotted" | "dashdot";
export type Arrow = "none" | "target" | "both";

export interface ResolvedStyle {
  stroke: string;
  width: number;
  dash: Dash;
  side: Side;
  arrow: Arrow;
  showLabel: boolean;
  label: string;
}

export interface RowGeometry {
  start: number;
  size: number;
}

export interface LayoutEdge {
  id: string;
  /** the rail track it belongs to; bundles never mix tracks (defaults to the id's `<track>:` prefix) */
  track?: string;
  /** visible row indices (already routed to the nearest visible ancestor) */
  from: number;
  to: number;
  style: ResolvedStyle;
  /** reduced for option-group members that are not chosen */
  opacity: number;
  /** option group this edge is a member of, if any */
  groupId?: string;
}

export interface LayoutGroup {
  id: string;
  label: string; // "1 of 3"
}

export interface EdgePath {
  id: string;
  /** set on a bundle's branch: the bundle it hangs from */
  bundleId?: string;
  side: Side;
  lane: number;
  d: string;
  y1: number;
  y2: number;
  x: number;
  style: ResolvedStyle;
  opacity: number;
  dasharray: string | undefined;
  label: { x: number; y: number; text: string } | null;
}

export interface GroupBrace {
  id: string;
  side: Side;
  d: string;
  label: { x: number; y: number; text: string };
}

/** A bundle's shared part (spec §8.15): the trunk in its lane, twice the track's width, the stub to the
 *  shared row, and the label once. Its members are drawn as branches (`EdgePath` with `bundleId`). */
export interface BundleTrunk {
  id: string;
  side: Side;
  lane: number;
  x: number;
  /** the trunk */
  d: string;
  /** the stub between the trunk and the row the members share */
  stub: string;
  /** the shared row is the members' origin, or their target */
  shared: "origin" | "target";
  sharedY: number;
  width: number;
  style: ResolvedStyle;
  dasharray: string | undefined;
  members: string[];
  label: { x: number; y: number; text: string } | null;
}

export interface RailLayout {
  leftGutter: number;
  rightGutter: number;
  paths: EdgePath[];
  trunks: BundleTrunk[];
  braces: GroupBrace[];
}

/** Administrator settings (spec §8.15, §8.26): a connector that fits an origin and a target bundle joins
 *  the `larger` one (a tie: the origin bundle) or always the `origin` bundle; option-group members are
 *  bundled (`bundle`, the group's label at the trunk) or keep their own lines and brace (`separate`). */
export interface BundleOptions {
  overlap: "larger" | "origin";
  groups: "bundle" | "separate";
}

export const DEFAULT_BUNDLES: BundleOptions = { overlap: "larger", groups: "bundle" };

export const LANE_GAP = 12;
export const GUTTER_PAD = 10;
/** where a connector meets a row: the middle of its first line, never below the middle of the row */
export const ANCHOR_Y = 16;

export function anchorY(row: RowGeometry): number {
  return row.start + Math.min(ANCHOR_Y, row.size / 2);
}

export function dashArray(dash: Dash, width: number): string | undefined {
  const w = Math.max(1, width);
  switch (dash) {
    case "dashed":
      return `${6 * w} ${4 * w}`;
    case "dotted":
      return `${w} ${2.5 * w}`;
    case "dashdot":
      return `${8 * w} ${3 * w} ${w} ${3 * w}`;
    default:
      return undefined;
  }
}

interface Span {
  edge: LayoutEdge;
  top: number;
  bottom: number;
  y1: number;
  y2: number;
}

/** Greedy interval packing: sorted by top (then longer first), each edge takes the lowest lane whose
 *  spans do not overlap it. Spans that only touch at a row still overlap — they share its anchor. */
export function packLanes<T extends { top: number; bottom: number }>(spans: T[]): Array<T & { lane: number }> {
  const sorted = [...spans].sort((a, b) => a.top - b.top || b.bottom - b.top - (a.bottom - a.top));
  const lanes: Array<Array<{ top: number; bottom: number }>> = [];
  return sorted.map((s) => {
    let lane = lanes.findIndex((occupied) => occupied.every((o) => s.bottom < o.top || s.top > o.bottom));
    if (lane < 0) {
      lanes.push([]);
      lane = lanes.length - 1;
    }
    lanes[lane]!.push(s);
    return { ...s, lane };
  });
}

function roundedPath(xEdge: number, xLane: number, y1: number, y2: number): string {
  const dx = xLane - xEdge;
  const dy = y2 - y1;
  const r = Math.max(0, Math.min(6, Math.abs(dx) / 2, Math.abs(dy) / 2));
  const sx = Math.sign(dx) || 1;
  const sy = Math.sign(dy) || 1;
  if (r === 0) return `M ${xEdge} ${y1} H ${xLane} V ${y2} H ${xEdge}`;
  return [
    `M ${xEdge} ${y1}`,
    `H ${xLane - sx * r}`,
    `Q ${xLane} ${y1} ${xLane} ${y1 + sy * r}`,
    `V ${y2 - sy * r}`,
    `Q ${xLane} ${y2} ${xLane - sx * r} ${y2}`,
    `H ${xEdge}`,
  ].join(" ");
}

interface Bundle {
  id: string;
  shared: "origin" | "target";
  row: number;
  members: Span[];
}

const trackOf = (e: LayoutEdge) => e.track ?? e.id.split(":")[0] ?? e.id;

/** Which connectors run along a shared trunk (spec §8.15): two or more of one track, with one label,
 *  leaving one row or entering one row. A connector that fits both kinds goes by `options.overlap`;
 *  what is left alone is drawn as a single line. */
export function bundleSpans(spans: Span[], options: BundleOptions): { bundles: Bundle[]; singles: Span[] } {
  const keyOf = (s: Span, shared: "origin" | "target") =>
    `${shared}|${trackOf(s.edge)}|${s.edge.style.label}|${shared === "origin" ? s.edge.from : s.edge.to}`;
  const eligible = (s: Span) => s.edge.from !== s.edge.to && !(options.groups === "separate" && s.edge.groupId);
  const count = new Map<string, number>();
  for (const s of spans) {
    if (!eligible(s)) continue;
    for (const shared of ["origin", "target"] as const) count.set(keyOf(s, shared), (count.get(keyOf(s, shared)) ?? 0) + 1);
  }
  const chosen = new Map<string, Span[]>();
  const singles: Span[] = [];
  for (const s of spans) {
    if (!eligible(s)) {
      singles.push(s);
      continue;
    }
    const origin = count.get(keyOf(s, "origin")) ?? 0;
    const target = count.get(keyOf(s, "target")) ?? 0;
    const key = origin >= 2 && (options.overlap === "origin" || origin >= target) ? keyOf(s, "origin") : target >= 2 ? keyOf(s, "target") : null;
    if (!key) {
      singles.push(s);
      continue;
    }
    chosen.set(key, [...(chosen.get(key) ?? []), s]);
  }
  const bundles: Bundle[] = [];
  for (const [key, members] of chosen) {
    // a bundle the other kind took all but one member from is a single line again
    if (members.length < 2) {
      singles.push(...members);
      continue;
    }
    const shared = key.startsWith("origin|") ? "origin" : "target";
    const first = members[0]!.edge;
    bundles.push({ id: `bundle:${key}`, shared, row: shared === "origin" ? first.from : first.to, members });
  }
  return { bundles, singles };
}

/** A branch between a bundle's trunk at `xLane` and the row at `y` (`xEdge`); it bends into the trunk
 *  when it is the trunk's top (`bend` 1) or bottom (`bend` -1) end, and meets it straight otherwise. */
function branchPath(xEdge: number, xLane: number, y: number, bend: -1 | 0 | 1): string {
  const r = bend === 0 ? 0 : Math.min(6, Math.abs(xLane - xEdge) / 2);
  const sx = Math.sign(xEdge - xLane) || 1;
  if (r === 0) return `M ${xLane} ${y} H ${xEdge}`;
  return `M ${xLane} ${y + bend * r} Q ${xLane} ${y} ${xLane + sx * r} ${y} H ${xEdge}`;
}

/** Put each label in the longest stretch of its lane that no horizontal crosses: the connectors in outer
 *  lanes reach the content across the inner ones, and a bundle's own branches meet its trunk. */
function placeLabels(paths: EdgePath[], trunks: BundleTrunk[]): void {
  const crossings = (side: Side, lane: number) => [
    ...paths.filter((p) => p.side === side && p.lane > lane).flatMap((p) => [p.y1, p.y2]),
    ...trunks.filter((t) => t.side === side && t.lane > lane).map((t) => t.sharedY),
  ];
  const middle = (top: number, bottom: number, cuts: number[]) => {
    const ys = [top, ...cuts.filter((y) => y > top && y < bottom).sort((a, b) => a - b), bottom];
    let best = 0;
    for (let i = 1; i < ys.length; i++) if (ys[i]! - ys[i - 1]! > ys[best + 1]! - ys[best]!) best = i - 1;
    return (ys[best]! + ys[best + 1]!) / 2;
  };
  for (const p of paths) {
    if (p.label) p.label.y = middle(Math.min(p.y1, p.y2), Math.max(p.y1, p.y2), crossings(p.side, p.lane));
  }
  for (const t of trunks) {
    if (!t.label) continue;
    const own = paths.filter((p) => p.bundleId === t.id).map((p) => (t.shared === "origin" ? p.y2 : p.y1));
    const ys = [t.sharedY, ...own];
    t.label.y = middle(Math.min(...ys), Math.max(...ys), [...ys, ...crossings(t.side, t.lane)]);
  }
}

export function layoutRail(rows: RowGeometry[], edges: LayoutEdge[], groups: LayoutGroup[] = [], options: BundleOptions = DEFAULT_BUNDLES): RailLayout {
  const bySide: Record<Side, Span[]> = { left: [], right: [] };
  for (const edge of edges) {
    const a = rows[edge.from];
    const b = rows[edge.to];
    if (!a || !b) continue;
    const y1 = anchorY(a);
    // an edge within one row (a block linked to itself, or both ends routed to one collapsed group)
    // still draws as a small loop
    const y2 = edge.from === edge.to ? y1 + 10 : anchorY(b);
    bySide[edge.style.side].push({ edge, y1, y2, top: Math.min(y1, y2), bottom: Math.max(y1, y2) });
  }

  const paths: EdgePath[] = [];
  const trunks: BundleTrunk[] = [];
  const gutters: Record<Side, number> = { left: 0, right: 0 };
  for (const side of ["left", "right"] as const) {
    const { bundles, singles } = bundleSpans(bySide[side], options);
    // a bundle takes one lane over all its rows
    const units = [
      ...singles.map((s) => ({ top: s.top, bottom: s.bottom, single: s })),
      ...bundles.map((b) => ({ top: Math.min(...b.members.map((m) => m.top)), bottom: Math.max(...b.members.map((m) => m.bottom)), bundle: b })),
    ];
    const packed = packLanes(units);
    const laneCount = packed.reduce((m, p) => Math.max(m, p.lane + 1), 0);
    const gutter = laneCount ? GUTTER_PAD + laneCount * LANE_GAP : 0;
    gutters[side] = gutter;
    // lane 0 is closest to the content
    const xEdge = side === "left" ? gutter : 0;
    const laneX = (lane: number) => (side === "left" ? gutter - (lane + 1) * LANE_GAP : (lane + 1) * LANE_GAP);
    const labelX = (x: number) => x + (side === "left" ? -3 : 3);
    for (const p of packed) {
      const x = laneX(p.lane);
      if ("single" in p && p.single) {
        const s = p.single;
        const { style } = s.edge;
        paths.push({
          id: s.edge.id,
          side,
          lane: p.lane,
          d: roundedPath(xEdge, x, s.y1, s.y2),
          y1: s.y1,
          y2: s.y2,
          x,
          style,
          opacity: s.edge.opacity,
          dasharray: dashArray(style.dash, style.width),
          label: style.showLabel && style.label ? { x: labelX(x), y: (s.y1 + s.y2) / 2, text: style.label } : null,
        });
        continue;
      }
      const b = (p as { bundle: Bundle }).bundle;
      const style = b.members[0]!.edge.style;
      const sharedY = b.shared === "origin" ? b.members[0]!.y1 : b.members[0]!.y2;
      const ends = b.members.map((m) => (b.shared === "origin" ? m.y2 : m.y1));
      const top = Math.min(sharedY, ...ends);
      const bottom = Math.max(sharedY, ...ends);
      const bend = (y: number): -1 | 0 | 1 => (y === top ? 1 : y === bottom ? -1 : 0);
      const r = (y: number) => (bend(y) === 0 ? 0 : Math.min(6, Math.abs(x - xEdge) / 2));
      const trunkTop = top + r(top);
      const trunkBottom = bottom - r(bottom);
      for (const m of b.members) {
        const y = b.shared === "origin" ? m.y2 : m.y1;
        // several members may end on one row (two links to one block): they draw on top of each other
        paths.push({
          id: m.edge.id,
          bundleId: b.id,
          side,
          lane: p.lane,
          d: branchPath(xEdge, x, y, y === sharedY ? 0 : bend(y)),
          y1: m.y1,
          y2: m.y2,
          x,
          style: m.edge.style,
          opacity: m.edge.opacity,
          dasharray: dashArray(style.dash, style.width),
          label: null,
        });
      }
      trunks.push({
        id: b.id,
        side,
        lane: p.lane,
        x,
        d: `M ${x} ${trunkTop} V ${trunkBottom}`,
        stub: branchPath(xEdge, x, sharedY, bend(sharedY)),
        shared: b.shared,
        sharedY,
        width: style.width * 2,
        style,
        dasharray: dashArray(style.dash, style.width * 2),
        members: b.members.map((m) => m.edge.id),
        label: style.showLabel && style.label ? { x: labelX(x) + (side === "left" ? -style.width / 2 : style.width / 2), y: (top + bottom) / 2, text: style.label } : null,
      });
    }
  }

  placeLabels(paths, trunks);

  // an option group's brace spans its member lanes just above their shared source anchor; members
  // that run along one trunk carry the group's label at the trunk's shared end instead
  const braces: GroupBrace[] = [];
  for (const group of groups) {
    const members = paths.filter((p) => edges.find((e) => e.id === p.id)?.groupId === group.id);
    if (members.length < 2) continue;
    for (const side of ["left", "right"] as const) {
      const own = members.filter((m) => m.side === side);
      if (own.length < 2) continue;
      const bundle = own[0]!.bundleId;
      if (bundle && own.every((m) => m.bundleId === bundle)) {
        const trunk = trunks.find((t) => t.id === bundle)!;
        braces.push({ id: group.id, side, d: "", label: { x: trunk.x, y: trunk.sharedY - 6, text: group.label } });
        continue;
      }
      const xs = own.map((m) => m.x);
      const y = Math.min(...own.map((m) => m.y1)) - 5;
      const lo = Math.min(...xs);
      const hi = Math.max(...xs);
      braces.push({
        id: group.id,
        side,
        d: `M ${lo} ${y + 3} V ${y} H ${hi} V ${y + 3}`,
        label: { x: (lo + hi) / 2, y: y - 3, text: group.label },
      });
    }
  }
  return { leftGutter: gutters.left, rightGutter: gutters.right, paths, trunks, braces };
}

/** "1 of 3", "any of 3", "at least 1 of 3", "2–3 of 4" (spec §8.15). */
export function groupLabel(minChoice: number, maxChoice: number | null, members: number): string {
  if (maxChoice === 1) return `1 of ${members}`;
  if (maxChoice === null) return minChoice <= 0 ? `any of ${members}` : `at least ${minChoice} of ${members}`;
  return minChoice === maxChoice ? `${maxChoice} of ${members}` : `${minChoice}–${maxChoice} of ${members}`;
}
